import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getInvoiceNewById, getLabOrderByVcReference } from 'service/api';
import { Card, Col, Container, Row, CardBody, Table } from 'reactstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Viewinvoice = () => {
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [labOrder, setLabOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    const formatDate = (value, locale = 'en-ZA', options) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(locale, options);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getInvoiceNewById(id);
                const inv = response?.data || null;
                setInvoice(inv);
                // Fetch linked lab order for lens/frame details
                if (inv?.vc_reference) {
                    try {
                        const labRes = await getLabOrderByVcReference(inv.vc_reference);
                        setLabOrder(labRes?.data || null);
                    } catch (_) { }
                }
            } catch (error) {
                console.error('Error fetching invoice:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const loadImageAsBase64 = (url, callback) => {
        if (!url) { callback(null); return; }
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width; canvas.height = img.height;
            canvas.getContext('2d').drawImage(img, 0, 0);
            callback(canvas.toDataURL('image/jpeg'));
        };
        img.onerror = () => callback(null);
        img.src = url;
    };

    const handleDownloadPDF = () => {
        const clinic = invoice.clinic_id || {};
        let logoURL = null;
        if (clinic.logo) {
            const filename = clinic.logo.split(/[\\/]/).pop();
            logoURL = `https://oapi.ehrhub.co.za/uploads/${filename}`;
        }
        loadImageAsBase64(logoURL, (base64Logo) => buildPDF(base64Logo));
    };

    const buildPDF = (base64Logo) => {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pw = doc.internal.pageSize.getWidth();
        const ph = doc.internal.pageSize.getHeight();
        const patient = invoice.patient_id || {};
        const clinic  = invoice.clinic_id  || {};
        const patientName = [patient.title, patient.firstname, patient.lastname].filter(Boolean).join(' ');

        // ── Palette: professional charcoal + single accent ──
        const ACCENT  = [41, 98, 255];    // #2962ff  (blue accent — only on key elements)
        const DARK    = [31, 41, 55];     // #1f2937
        const MID     = [107, 114, 128];  // #6b7280
        const LIGHT   = [249, 250, 251];  // #f9fafb
        const BORDER  = [229, 231, 235];  // #e5e7eb
        const TH_BG   = [55, 65, 81];     // #374151  (table headers — dark gray)
        const WHITE   = [255, 255, 255];

        let y = 10;

        // ══════════════════════════════════════════
        // HEADER  (white background — clean)
        // ══════════════════════════════════════════
        const logoW = 28, logoH = 28;
        if (base64Logo) {
            try { doc.addImage(base64Logo, 'JPEG', 10, 8, logoW, logoH); } catch (_) {}
        }
        const nameX = base64Logo ? 10 + logoW + 5 : 10;

        // Clinic name
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(15);
        doc.setTextColor(...DARK);
        doc.text(clinic.name || 'TAX INVOICE', nameX, 16);

        // Clinic meta
        const metaLines = [
            ...(clinic.address || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean),
            clinic.email, clinic.contactno
        ].filter(Boolean);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...MID);
        metaLines.forEach((line, i) => doc.text(line, nameX, 22 + i * 4.5));

        // Right side — TAX INVOICE label + number
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.setTextColor(...DARK);
        doc.text('TAX INVOICE', pw - 10, 16, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...ACCENT);
        doc.text(invoice.invoice_no || '', pw - 10, 24, { align: 'right' });

        const headerH = Math.max(38, 22 + metaLines.length * 4.5);
        y = headerH;

        // Bottom border of header
        doc.setDrawColor(...ACCENT);
        doc.setLineWidth(0.8);
        doc.line(10, y, pw - 10, y);
        y += 7;

        // ══════════════════════════════════════════
        // META ROW  (Date / Type / Place)
        // ══════════════════════════════════════════
        const colW3 = (pw - 20) / 3;
        [
            { label: 'DATE OF SERVICE', value: formatDate(invoice.service_date || invoice.createdAt, 'en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) || '-' },
            { label: 'INVOICE TYPE',    value: invoice.invoice_type  || '-' },
            { label: 'PLACE OF SERVICE',value: invoice.service_place || '-' },
        ].forEach((col, i) => {
            const cx = 10 + i * colW3;
            doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...MID);
            doc.text(col.label, cx, y);
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...DARK);
            doc.text(col.value, cx, y + 5.5);
        });
        y += 14;

        // Thin divider
        doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
        doc.line(10, y, pw - 10, y);
        y += 6;

        // ══════════════════════════════════════════
        // BILL TO  |  TREATING PROVIDER  (two boxes)
        // ══════════════════════════════════════════
        const boxW  = (pw - 26) / 2;
        const box2X = 10 + boxW + 6;
        const boxH  = 30;

        const drawInfoBox = (bx, by, bw, bh, title, lines) => {
            // box border only — light fill
            doc.setFillColor(...LIGHT);
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.2);
            doc.rect(bx, by, bw, bh, 'FD');
            // Title — dark gray strip
            doc.setFillColor(...TH_BG);
            doc.rect(bx, by, bw, 6.5, 'F');
            doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...WHITE);
            doc.text(title, bx + bw / 2, by + 4.5, { align: 'center' });
            // Content
            lines.forEach((ln, i) => {
                if (!ln.value) return;
                const lY = by + 10 + i * 5;
                doc.setFont('helvetica', i === 0 ? 'bold' : 'normal');
                doc.setFontSize(i === 0 ? 8.5 : 8);
                doc.setTextColor(...(i === 0 ? DARK : MID));
                doc.text((ln.label ? ln.label + ': ' : '') + ln.value, bx + 4, lY);
            });
        };

        drawInfoBox(10, y, boxW, boxH, 'BILL TO', [
            { label: '',       value: patientName || '-' },
            { label: 'MPI',    value: patient.mpino  || '-' },
            { label: 'Tel',    value: patient.mobile || '-' },
            { label: 'Email',  value: patient.email  || '-' },
        ]);
        drawInfoBox(box2X, y, boxW, boxH, 'TREATING PROVIDER', [
            { label: '',            value: invoice.treating_provider || '-' },
            { label: 'Diagnosis',   value: invoice.diagnosis_code   || '-' },
            { label: 'Service',     value: invoice.service_place    || '-' },
        ]);
        y += boxH + 8;

        // ══════════════════════════════════════════
        // PRESCRIPTION TABLE
        // ══════════════════════════════════════════
        const scriptRows = invoice.script_rows || [];
        if (scriptRows.length > 0) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...DARK);
            doc.text('PRESCRIPTION / SCRIPT', 10, y);
            y += 4;
            doc.autoTable({
                startY: y,
                head: [['Eye', 'Lens', 'Status', 'Spherical', 'Cylinder', 'Axis', 'Add', 'Prism']],
                body: scriptRows.map(r => [r.eye||'-', r.lens||'-', r.status||'-', r.spherical||'-', r.cylinder||'-', r.axis||'-', r.add||'-', r.prism||'-']),
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 1.8, textColor: DARK },
                headStyles: { fillColor: TH_BG, textColor: WHITE, fontStyle: 'bold', fontSize: 7.5 },
                bodyStyles: { lineColor: BORDER, lineWidth: 0.15 },
                alternateRowStyles: { fillColor: LIGHT },
                margin: { left: 10, right: 10 }, tableWidth: pw - 20,
            });
            y = doc.lastAutoTable.finalY + 8;
        }

        // ══════════════════════════════════════════
        // BILLING TABLE
        // ══════════════════════════════════════════
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...DARK);
        doc.text('BILLING DETAILS', 10, y);
        y += 4;

        const pdfBillingRows = (invoice.invoice_rows || [])
            .filter(r => r.qty || r.line_type || r.description || r.tarif_code || r.price_info)
            .map((r, i) => [
                i + 1,
                r.qty || '-',
                r.line_type || '-',
                r.description || '-',
                r.tarif_code || '-',
                r.diag_code || '-',
                r.price_info ? `R ${parseFloat(r.price_info).toFixed(2)}` : '-',
            ]);

        doc.autoTable({
            startY: y,
            head: [['#', 'QTY', 'Line Type', 'Description', 'Tarif Code', 'Diag Code', 'Amount']],
            body: pdfBillingRows.length ? pdfBillingRows : [['-', '-', '-', 'No billing items', '-', '-', '-']],
            theme: 'grid',
            styles: { fontSize: 8.5, cellPadding: 2, textColor: DARK },
            headStyles: { fillColor: TH_BG, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
            bodyStyles: { lineColor: BORDER, lineWidth: 0.15 },
            alternateRowStyles: { fillColor: LIGHT },
            columnStyles: {
                0: { cellWidth: 8,  halign: 'center' },
                1: { cellWidth: 12, halign: 'center' },
                2: { cellWidth: 26 },
                3: { cellWidth: 'auto' },
                4: { cellWidth: 22 },
                5: { cellWidth: 22 },
                6: { cellWidth: 24, halign: 'right' },
            },
            margin: { left: 10, right: 10 }, tableWidth: pw - 20,
        });
        y = doc.lastAutoTable.finalY + 6;

        // ══════════════════════════════════════════
        // COMPACT LENS + FRAME DETAILS  (if available)
        // ══════════════════════════════════════════
        const lo = labOrder; // labOrder from component state — pass it in scope via closure
        if (lo) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...DARK);
            doc.text('LENS & FRAME DETAILS', 10, y);
            y += 4;
            const lensFrameData = [];
            const odMat = lo.lens_material_od || ''; const odCoat = lo.lens_type_od ? lo.lens_type_od.toUpperCase() : ''; const odVis = lo.lens_vision_od?.vision || '';
            const osMat = lo.lens_material_os || ''; const osCoat = lo.lens_type_os ? lo.lens_type_os.toUpperCase() : ''; const osVis = lo.lens_vision_os?.vision || '';
            if (odMat || odCoat) lensFrameData.push(['OD (Right Lens)', [odVis, odMat, odCoat].filter(Boolean).join(' · '), invoice.od_lens_price > 0 ? `R ${parseFloat(invoice.od_lens_price).toFixed(2)}` : '']);
            if (osMat || osCoat) lensFrameData.push(['OS (Left Lens)',  [osVis, osMat, osCoat].filter(Boolean).join(' · '), invoice.os_lens_price > 0 ? `R ${parseFloat(invoice.os_lens_price).toFixed(2)}` : '']);
            const frBrand = lo.frame_brand || ''; const frColor = lo.frame_color || ''; const frCode = lo.framecode || '';
            if (frBrand || frCode) lensFrameData.push(['Frame', [frBrand, frColor, frCode].filter(Boolean).join(' · '), parseFloat(invoice.frame_price||0) > 0 ? `R ${parseFloat(invoice.frame_price).toFixed(2)}` : '']);
            if (lensFrameData.length) {
                doc.autoTable({
                    startY: y,
                    head: [['Item', 'Details', 'Price']],
                    body: lensFrameData,
                    theme: 'plain',
                    styles: { fontSize: 8, cellPadding: 2, textColor: DARK },
                    headStyles: { fillColor: LIGHT, textColor: MID, fontStyle: 'bold', fontSize: 7.5, lineColor: BORDER, lineWidth: 0.2 },
                    bodyStyles: { lineColor: BORDER, lineWidth: 0.15 },
                    columnStyles: { 0: { cellWidth: 32, fontStyle: 'bold' }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 28, halign: 'right' } },
                    margin: { left: 10, right: 10 }, tableWidth: pw - 20,
                });
                y = doc.lastAutoTable.finalY + 6;
            }
        }

        // ══════════════════════════════════════════
        // TOTALS BLOCK  (right-aligned, clean)
        // ══════════════════════════════════════════
        const subtotalAmt = parseFloat(invoice.subtotal)        || 0;
        const discountAmt = parseFloat(invoice.discount_amount) || 0;
        const vatAmt      = parseFloat(invoice.vat_amount)      || 0;
        const grandTotal  = parseFloat(invoice.total_amount)    || 0;
        const vatLabel    = `VAT (${invoice.vat_percent || '15'}%)`;
        const discLabel   = invoice.discount_type === 'percent'
            ? `Discount (${invoice.discount_value || 0}%)` : 'Discount (Fixed)';

        const totW  = 85;
        const totX  = pw - 10 - totW;
        const rowHs = 6.5;
        // Max width for label text before it would hit the amount column
        const labelMaxW = totW - 32;

        // Small breakdown rows — truncate label to prevent overlap
        const drawSmallRow = (label, value) => {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MID);
            // Clip label to one line within available width
            const clipped = doc.splitTextToSize(label, labelMaxW)[0];
            doc.text(clipped, totX + 4, y + 4);
            doc.text(value, totX + totW - 4, y + 4, { align: 'right' });
            y += 5.5;
        };
        (invoice.invoice_rows || []).filter(r => r.description || r.price_info).forEach(r => {
            drawSmallRow(r.description || r.line_type || '—', `R ${((parseFloat(r.price_info)||0)*(parseFloat(r.qty)||1)).toFixed(2)}`);
        });
        if (parseFloat(invoice.od_lens_price||0) > 0) drawSmallRow('OD Lens', `R ${parseFloat(invoice.od_lens_price).toFixed(2)}`);
        if (parseFloat(invoice.os_lens_price||0) > 0) drawSmallRow('OS Lens', `R ${parseFloat(invoice.os_lens_price).toFixed(2)}`);
        if (parseFloat(invoice.frame_price||0)   > 0) drawSmallRow('Frame',   `R ${parseFloat(invoice.frame_price).toFixed(2)}`);

        // Dashed separator
        doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
        doc.setLineDash([1, 1.5]);
        doc.line(totX + 3, y, totX + totW - 3, y);
        doc.setLineDash([]);
        y += 3;

        // Summary rows helper
        const drawSumRow = (label, value, bold = false, valColorRGB = null) => {
            doc.setFillColor(bold ? LIGHT[0] : WHITE[0], bold ? LIGHT[1] : WHITE[1], bold ? LIGHT[2] : WHITE[2]);
            doc.setDrawColor(...BORDER); doc.setLineWidth(0.15);
            doc.rect(totX, y, totW, rowHs, 'FD');
            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setFontSize(8.5); doc.setTextColor(...MID);
            doc.text(label, totX + 4, y + 4.5);
            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setTextColor(...(valColorRGB || DARK));
            doc.text(value, totX + totW - 4, y + 4.5, { align: 'right' });
            y += rowHs;
        };

        drawSumRow('Subtotal', `R ${subtotalAmt.toFixed(2)}`);
        if (discountAmt > 0) drawSumRow(discLabel, `- R ${discountAmt.toFixed(2)}`, false, [220, 38, 38]);
        drawSumRow(vatLabel, `+ R ${vatAmt.toFixed(2)}`, false, [21, 128, 61]);

        // Grand Total — dark background
        doc.setFillColor(...DARK);
        doc.rect(totX, y, totW, 11, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...WHITE);
        doc.text('GRAND TOTAL', totX + 4, y + 7);
        doc.setFontSize(11);
        doc.text(`R ${grandTotal.toFixed(2)}`, totX + totW - 4, y + 7, { align: 'right' });
        y += 11;

        // ══════════════════════════════════════════
        // FOOTER  (minimal, dark)
        // ══════════════════════════════════════════
        const footY = ph - 14;
        doc.setFillColor(...DARK);
        doc.rect(0, footY, pw, 14, 'F');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(180, 180, 180);
        doc.text('Thank you for choosing us — we appreciate your business.', pw / 2, footY + 5, { align: 'center' });
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...WHITE);
        const footerInfo = [clinic.name, clinic.email, clinic.contactno].filter(Boolean).join('   |   ');
        doc.text(footerInfo, pw / 2, footY + 10.5, { align: 'center' });

        doc.save(`Invoice_${invoice.invoice_no || invoice._id}.pdf`);
    };

    if (loading) return <div className="page-content text-center py-5"><div className="spinner-border text-primary" /></div>;
    if (!invoice) return <div className="page-content text-center py-5"><h5 className="text-danger">Invoice not found.</h5></div>;

    const patient = invoice.patient_id || {};
    const clinic  = invoice.clinic_id  || {};
    const patientName = [patient.title, patient.firstname, patient.lastname].filter(Boolean).join(' ');
    const billingRows = (invoice.invoice_rows || []).filter(r => r.qty || r.line_type || r.description || r.tarif_code || r.price_info);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* Top bar */}
                    <div className="d-flex justify-content-between align-items-center mb-3 no-print">
                        <h3 className="fw-bold text-dark mb-0">Invoice</h3>
                        <div>
                            <button className="btn btn-success me-2" onClick={handleDownloadPDF}>
                                <i className="mdi mdi-download me-1"></i> Download PDF
                            </button>
                            <Link to="/List/Invoice" className="btn btn-primary">
                                <i className="mdi mdi-arrow-left me-1"></i> Back to List
                            </Link>
                        </div>
                    </div>

                    <Card className="shadow">
                        <CardBody className="p-0">

                            {/* ── TOP BANNER ── */}
                            <div style={{ background: '#DC4C64', borderRadius: '8px 8px 0 0', padding: '18px 24px' }}>
                                <Row className="align-items-center">
                                    <Col md={7}>
                                        {clinic.name ? (
                                            <>
                                                <h2 className="fw-bold mb-0" style={{ color: '#fff', letterSpacing: 1, fontSize: 22 }}>{clinic.name}</h2>
                                                {clinic.address && <p className="mb-0" style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{clinic.address}</p>}
                                                <p className="mb-0" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
                                                    {clinic.contactno && <><i className="mdi mdi-phone me-1"></i>{clinic.contactno}</>}
                                                    {clinic.contactno && clinic.email && '  |  '}
                                                    {clinic.email && <><i className="mdi mdi-email me-1"></i>{clinic.email}</>}
                                                </p>
                                            </>
                                        ) : (
                                            <h2 className="fw-bold mb-0" style={{ color: '#fff', letterSpacing: 2, fontSize: 22 }}>TAX INVOICE</h2>
                                        )}
                                    </Col>
                                    <Col md={5} className="text-end">
                                        <div style={{ color: '#fff' }}>
                                            <div style={{ fontSize: 13, opacity: 0.8 }}>INVOICE NO</div>
                                            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>{invoice.invoice_no || '—'}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* ── INVOICE META STRIP ── */}
                            <div style={{ background: '#f9f0f2', borderBottom: '1px solid #f0d0d8', padding: '10px 24px' }}>
                                <Row>
                                    <Col xs={6} md={3}>
                                        <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase' }}>Invoice Type</div>
                                        <span className={`badge bg-${invoice.invoice_type === 'Medical Aid' ? 'success' : invoice.invoice_type === 'Cash' ? 'warning' : 'info'}`} style={{ fontSize: 12 }}>
                                            {invoice.invoice_type || '—'}
                                        </span>
                                    </Col>
                                    <Col xs={6} md={3}>
                                        <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase' }}>Date of Service</div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{formatDate(invoice.service_date || invoice.createdAt) || '—'}</div>
                                    </Col>
                                    {invoice.vc_reference && (
                                        <Col xs={6} md={3}>
                                            <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase' }}>VC Reference</div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#DC4C64' }}>{invoice.vc_reference}</div>
                                        </Col>
                                    )}
                                    {invoice.lab_order_no && (
                                        <Col xs={6} md={3}>
                                            <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase' }}>Lab Order No</div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>{invoice.lab_order_no}</div>
                                        </Col>
                                    )}
                                </Row>
                            </div>

                            <div className="p-4">

                            {/* ── PATIENT & SERVICE INFO ── */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                                        <h6 className="fw-bold text-danger mb-2">
                                            <i className="mdi mdi-account me-1"></i> Patient Details
                                        </h6>
                                        <table className="w-100" style={{ fontSize: 13 }}>
                                            <tbody>
                                                <tr><td className="text-muted" style={{ width: 130 }}>Patient Name:</td><td className="fw-bold">{patientName || '—'}</td></tr>
                                                <tr><td className="text-muted">MPI No:</td><td>{patient.mpino || '—'}</td></tr>
                                                <tr><td className="text-muted">Mobile:</td><td>{patient.mobile || '—'}</td></tr>
                                                <tr><td className="text-muted">Email:</td><td>{patient.email || '—'}</td></tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                                        <h6 className="fw-bold text-danger mb-2">
                                            <i className="mdi mdi-stethoscope me-1"></i> Service Details
                                        </h6>
                                        <table className="w-100" style={{ fontSize: 13 }}>
                                            <tbody>
                                                <tr><td className="text-muted" style={{ width: 150 }}>Treating Provider:</td><td>{invoice.treating_provider || '—'}</td></tr>
                                                <tr><td className="text-muted">Date of Service:</td><td>{formatDate(invoice.service_date) || '—'}</td></tr>
                                                <tr><td className="text-muted">Place of Service:</td><td>{invoice.service_place || '—'}</td></tr>
                                                <tr><td className="text-muted">Diagnosis Code:</td><td>{invoice.diagnosis_code || '—'}</td></tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>

                            {/* ── SCRIPT / PRESCRIPTION ── */}
                            <h6 className="fw-bold text-danger mb-2">
                                <i className="mdi mdi-glasses me-1"></i> Prescription / Script Details
                            </h6>
                            <Table bordered responsive className="mb-4" style={{ fontSize: 13 }}>
                                <thead style={{ backgroundColor: '#f46a6a', color: '#fff' }}>
                                    <tr>
                                        <th>Eye</th>
                                        <th>Lens</th>
                                        <th>Status</th>
                                        <th>Spherical</th>
                                        <th>Cylinder</th>
                                        <th>Axis</th>
                                        <th>Add</th>
                                        <th>Prism</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.script_rows && invoice.script_rows.length > 0
                                        ? invoice.script_rows.map((row, i) => (
                                            <tr key={i}>
                                                <td><strong>{row.eye || '-'}</strong></td>
                                                <td>{row.lens || '-'}</td>
                                                <td>{row.status || '-'}</td>
                                                <td>{row.spherical || '-'}</td>
                                                <td>{row.cylinder || '-'}</td>
                                                <td>{row.axis || '-'}</td>
                                                <td>{row.add || '-'}</td>
                                                <td>{row.prism || '-'}</td>
                                            </tr>
                                        ))
                                        : <tr><td colSpan={8} className="text-center text-muted"></td></tr>
                                    }
                                </tbody>
                            </Table>

                            {/* ── LAB LENS + FRAME DETAILS ── */}
                            {labOrder && (
                                <>
                                    <Row className="mb-3">
                                        {/* OD Lens */}
                                        <Col md={4}>
                                            <div style={{ border: '1.5px solid #DC4C64', borderRadius: 8, padding: '10px 14px', background: '#fff8f8', height: '100%' }}>
                                                <div style={{ fontWeight: 700, color: '#DC4C64', fontSize: 13, marginBottom: 8 }}>
                                                    <i className="mdi mdi-eye me-1"></i> OD — Right Lens
                                                </div>
                                                <table style={{ width: '100%', fontSize: 12 }}>
                                                    <tbody>
                                                        {labOrder.lens_material_od && <tr><td style={{ color: '#888', paddingBottom: 3, width: 90 }}>Material</td><td style={{ fontWeight: 500 }}>{labOrder.lens_material_od}</td></tr>}
                                                        {labOrder.lens_vision_od?.vision && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Vision</td><td>{labOrder.lens_vision_od.vision}</td></tr>}
                                                        {labOrder.lens_subvision_od?.sub_type && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Sub-Type</td><td>{labOrder.lens_subvision_od.sub_type}</td></tr>}
                                                        {labOrder.lens_type_od && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Coating</td><td>{labOrder.lens_type_od.toUpperCase()}</td></tr>}
                                                        {labOrder.extra_od && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Photochromic</td><td>{labOrder.extra_od === 'yes' ? 'Yes' : 'No'}</td></tr>}
                                                        {parseFloat(invoice.od_lens_price || 0) > 0 && (
                                                            <tr style={{ borderTop: '1px solid #f0c0c0' }}>
                                                                <td style={{ color: '#DC4C64', fontWeight: 700, paddingTop: 5 }}>OD Total</td>
                                                                <td style={{ color: '#DC4C64', fontWeight: 700, paddingTop: 5 }}>R {parseFloat(invoice.od_lens_price).toFixed(2)}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>

                                        {/* OS Lens */}
                                        <Col md={4}>
                                            <div style={{ border: '1.5px solid #3b82f6', borderRadius: 8, padding: '10px 14px', background: '#f5f8ff', height: '100%' }}>
                                                <div style={{ fontWeight: 700, color: '#3b82f6', fontSize: 13, marginBottom: 8 }}>
                                                    <i className="mdi mdi-eye me-1"></i> OS — Left Lens
                                                </div>
                                                <table style={{ width: '100%', fontSize: 12 }}>
                                                    <tbody>
                                                        {labOrder.lens_material_os && <tr><td style={{ color: '#888', paddingBottom: 3, width: 90 }}>Material</td><td style={{ fontWeight: 500 }}>{labOrder.lens_material_os}</td></tr>}
                                                        {labOrder.lens_vision_os?.vision && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Vision</td><td>{labOrder.lens_vision_os.vision}</td></tr>}
                                                        {labOrder.lens_subvision_os?.sub_type && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Sub-Type</td><td>{labOrder.lens_subvision_os.sub_type}</td></tr>}
                                                        {labOrder.lens_type_os && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Coating</td><td>{labOrder.lens_type_os.toUpperCase()}</td></tr>}
                                                        {labOrder.extra_os && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Photochromic</td><td>{labOrder.extra_os === 'yes' ? 'Yes' : 'No'}</td></tr>}
                                                        {parseFloat(invoice.os_lens_price || 0) > 0 && (
                                                            <tr style={{ borderTop: '1px solid #b0c8f8' }}>
                                                                <td style={{ color: '#3b82f6', fontWeight: 700, paddingTop: 5 }}>OS Total</td>
                                                                <td style={{ color: '#3b82f6', fontWeight: 700, paddingTop: 5 }}>R {parseFloat(invoice.os_lens_price).toFixed(2)}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>

                                        {/* Frame */}
                                        <Col md={4}>
                                            <div style={{ border: '1.5px solid #16a34a', borderRadius: 8, padding: '10px 14px', background: '#f0fdf4', height: '100%' }}>
                                                <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 13, marginBottom: 8 }}>
                                                    <i className="mdi mdi-glasses me-1"></i> Frame Details
                                                </div>
                                                <table style={{ width: '100%', fontSize: 12 }}>
                                                    <tbody>
                                                        {labOrder.frame_brand && <tr><td style={{ color: '#888', paddingBottom: 3, width: 90 }}>Brand</td><td style={{ fontWeight: 500 }}>{labOrder.frame_brand}</td></tr>}
                                                        {labOrder.frame_color && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Color</td><td>{labOrder.frame_color}</td></tr>}
                                                        {labOrder.frame_size && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Size</td><td>{labOrder.frame_size}</td></tr>}
                                                        {labOrder.frame_material && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Material</td><td>{labOrder.frame_material}</td></tr>}
                                                        {labOrder.frame_type && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Type</td><td>{labOrder.frame_type}</td></tr>}
                                                        {labOrder.framecode && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Code</td><td>{labOrder.framecode}</td></tr>}
                                                        {parseFloat(invoice.frame_price || 0) > 0 && (
                                                            <tr style={{ borderTop: '1px solid #86efac' }}>
                                                                <td style={{ color: '#16a34a', fontWeight: 700, paddingTop: 5 }}>Frame Price</td>
                                                                <td style={{ color: '#16a34a', fontWeight: 700, paddingTop: 5 }}>R {parseFloat(invoice.frame_price).toFixed(2)}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                    </Row>
                                    <hr className="mb-3" style={{ borderColor: '#eee' }} />
                                </>
                            )}

                            {/* ── INVOICE BILLING ROWS ── */}
                            <h6 className="fw-bold text-danger mb-2">
                                <i className="mdi mdi-receipt me-1"></i> Billing Details
                            </h6>
                            <Table bordered responsive className="mb-2" style={{ fontSize: 13 }}>
                                <thead style={{ backgroundColor: '#556ee6', color: '#fff' }}>
                                    <tr>
                                        <th>#</th>
                                        <th>QTY</th>
                                        <th>Line Type</th>
                                        <th>Description</th>
                                        <th>Tarif Code</th>
                                        <th>Diag. Code</th>
                                        <th className="text-end">Amount (R)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billingRows.length > 0
                                        ? billingRows.map((row, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td>{row.qty || '-'}</td>
                                                <td>{row.line_type || '-'}</td>
                                                <td>{row.description || '-'}</td>
                                                <td>{row.tarif_code || '-'}</td>
                                                <td>{row.diag_code || '-'}</td>
                                                <td className="text-end fw-bold">
                                                    {row.price_info ? `R ${parseFloat(row.price_info).toFixed(2)}` : '-'}
                                                </td>
                                            </tr>
                                        ))
                                        : <tr><td colSpan={7} className="text-center text-muted fst-italic" style={{ fontSize: 13 }}>No billing items</td></tr>
                                    }
                                </tbody>
                            </Table>

                            {/* ── TOTALS BREAKDOWN ── */}
                            <Row className="justify-content-end mb-4">
                                <Col md={4}>
                                    <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            {billingRows.map((row, i) => (
                                                <tr key={i}>
                                                    <td style={{ padding: '3px 0', color: '#888', fontSize: 12 }}>{row.description || row.line_type || '—'}</td>
                                                    <td style={{ textAlign: 'right', color: '#888', fontSize: 12 }}>R {((parseFloat(row.price_info) || 0) * (parseFloat(row.qty) || 1)).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                            {parseFloat(invoice.od_lens_price || 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '3px 0', color: '#888', fontSize: 12 }}>OD Lens</td>
                                                    <td style={{ textAlign: 'right', color: '#888', fontSize: 12 }}>R {parseFloat(invoice.od_lens_price).toFixed(2)}</td>
                                                </tr>
                                            )}
                                            {parseFloat(invoice.os_lens_price || 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '3px 0', color: '#888', fontSize: 12 }}>OS Lens</td>
                                                    <td style={{ textAlign: 'right', color: '#888', fontSize: 12 }}>R {parseFloat(invoice.os_lens_price).toFixed(2)}</td>
                                                </tr>
                                            )}
                                            {parseFloat(invoice.frame_price || 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '3px 0', color: '#888', fontSize: 12 }}>Frame</td>
                                                    <td style={{ textAlign: 'right', color: '#888', fontSize: 12 }}>R {parseFloat(invoice.frame_price).toFixed(2)}</td>
                                                </tr>
                                            )}
                                            <tr><td colSpan={2}><hr style={{ margin: '3px 0', borderTop: '1px dashed #ddd' }} /></td></tr>
                                            <tr>
                                                <td style={{ padding: '5px 0', color: '#555' }}>Subtotal</td>
                                                <td style={{ textAlign: 'right', fontWeight: '600' }}>R {invoice.subtotal || '0.00'}</td>
                                            </tr>
                                            {parseFloat(invoice.discount_amount) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '5px 0', color: '#555' }}>
                                                        Discount {invoice.discount_type === 'percent'
                                                            ? `(${invoice.discount_value}%)`
                                                            : `(Fixed)`}
                                                    </td>
                                                    <td style={{ textAlign: 'right', color: '#e74c3c', fontWeight: '600' }}>- R {invoice.discount_amount}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td style={{ padding: '5px 0', color: '#555' }}>VAT ({invoice.vat_percent || '15'}%)</td>
                                                <td style={{ textAlign: 'right', color: '#27ae60', fontWeight: '600' }}>+ R {invoice.vat_amount || '0.00'}</td>
                                            </tr>
                                            <tr style={{ borderTop: '2px solid #556ee6' }}>
                                                <td style={{ padding: '8px 0', fontWeight: '800', fontSize: '16px', color: '#556ee6' }}>Grand Total</td>
                                                <td style={{ textAlign: 'right', fontWeight: '800', fontSize: '18px', color: '#556ee6' }}>R {invoice.total_amount || '0.00'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </Col>
                            </Row>



                            </div>{/* end .p-4 */}
                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* Print styles */}
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .page-content { padding: 0 !important; }
                    .shadow { box-shadow: none !important; }
                }
            `}</style>
        </React.Fragment>
    );
};

export default Viewinvoice;
